import storefrontInstance from '@/api/instances/storefront.js';
import {fetchAndCache} from '@/utils/cached-promise.js';
import occInstance from '@/api/instances/occ.js';
import productData from '../../../../trekcommons/tests/jest/mock/product-data.json';
import {productDataMapper} from '@/utils/product/product-helpers.js';

const ArchivedStatus = Object.freeze({
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
  HYBRID: 'HYBRID',
});

const ProductApi = {
  /**
   * Get nearest retailers with availability for the given variant.
   * @param {object} options
   * @return {*}
   */
  async getRetailerAvailability(options) {
    const defaults = {
      productCode: '',
      isInStockOnline: true,
      retailersMax: 3,
      inStockOnly: false,
      locationText: '',
    };

    options = Object.assign(defaults, options);

    let url = `/rpa/${options.productCode}?retailers=${options.retailersMax}&isInStockOnline=${Boolean(
      options.isInStockOnline
    )}&inStockOnly=${options.inStockOnly}`;

    if (options.locationText) {
      url = `${url}&locationText=${options.locationText}`;
    }

    const response = await storefrontInstance.get(url, {
      headers: {'X-Requested-With': 'XMLHttpRequest'},
    });

    return response.data ?? {};
  },

  async fetchProductStatus(code) {
    const {data} = await storefrontInstance.get(`/v1/api/product/${code}/status/`);
    return data?.data;
  },

  generateEndpointUrl({archivedStatus, code, isB2b}) {
    const base = isB2b ? `/v1/api/product/${code}/full/` : `/products/${code}/full/`;
    if (archivedStatus === ArchivedStatus.ARCHIVED) {
      return `${base}?type=archive`;
    }

    if (archivedStatus === ArchivedStatus.HYBRID) {
      const isArchivedPdp = window.location.href.includes('/a/');
      return `${base}${isArchivedPdp ? '?type=archive' : ''}`;
    }
    return base;
  },

  async fetchFullProduct({code, isB2b, archivedStatus}) {
    const endpointUrl = this.generateEndpointUrl({archivedStatus, code, isB2b});

    let response;

    if (isB2b) {
      response = await storefrontInstance.get(endpointUrl);
    } else {
      response = await occInstance.get(endpointUrl);
    }

    const productResponseData = response?.data?.data ?? response?.data;

    productDataMapper(productResponseData);

    return productResponseData;
  },

  /**
   * Load product data by product's code.
   * @param {string} code
   * @return {Promise}
   */
  async loadProduct(code, isB2b) {
    const status = await this.fetchProductStatus(code);
    const productResponseData = await this.fetchFullProduct({code, isB2b, archivedStatus: status.archivedStatus});
    productResponseData.status = status;
    return productResponseData;
  },
  getExtendedProductDataById: async (id) => {
    return productData.find((product) => product.code === id);
  },

  /**
   * Check product IDs.
   * @param {array} ids
   */
  productIdsToCommaSeparated(ids) {
    if (!ids || !ids.length) {
      throw new Error('Product IDs cannot be empty!');
    }

    return ids.join(',');
  },

  /**
   * Batch load a list of products by given list of IDs.
   * @param {array} ids
   * @return {Promise<array>}
   */
  batchLoadProducts(ids) {
    const idsList = this.productIdsToCommaSeparated(ids);
    const endpointUrl = `/products/grid?codes=${idsList}`;

    return new Promise((resolve) => {
      fetchAndCache(`products-${idsList}`, () => {
        return storefrontInstance.get(endpointUrl);
      }).then((response) => {
        const data = response?.data?.data;
        let products = Array.isArray(data) ? data : [data];
        products.forEach((product) => {
          productDataMapper(product);
        });
        resolve(products ?? []);
      });
    });
  },

  /**
   * Load a full data of products grid by given list of IDs.
   * @param {array} ids
   * @return {Promise<object>}
   */
  loadSkuGrid(ids) {
    const idsList = this.productIdsToCommaSeparated(ids);
    const endpointUrl = `/order-copy-paste/grid?codes=${idsList}`;

    return new Promise((resolve) => {
      fetchAndCache(`products-${idsList}`, () => {
        return storefrontInstance.get(endpointUrl);
      }).then((response) => {
        resolve(response.data ?? {});
      });
    });
  },

  /**
   * Batch load allocated / back ordered counts for given list of product IDs.
   * @param {array} ids
   * @return {Promise<array>}
   */
  batchLoadAllocatedData(ids) {
    const idsList = this.productIdsToCommaSeparated(ids);
    const endpointUrl = `/products/allocated-lines?codes=${idsList}`;

    return new Promise((resolve) => {
      fetchAndCache(`allocated-lines-${idsList}`, () => {
        return storefrontInstance.get(endpointUrl);
      }).then((response) => {
        resolve(response?.data?.data ?? []);
      });
    });
  },

  /**
+   * Batch load a list of variants by given list of sku codes.
+   * @param {array} ids
+   * @return {Promise<array>}
+   */
  fetchVariantsPrices(ids) {
    const idsList = this.productIdsToCommaSeparated(ids);
    const endpointUrl = `/v1/api/batch/price/all/`;

    return new Promise((resolve) => {
      fetchAndCache(`products-prices-${idsList}`, () => {
        return storefrontInstance.post(endpointUrl, {variantCodesList: ids});
      }).then((response) => {
        resolve(response?.data?.data ?? []);
      });
    });
  },
  /* Required for B2C Tech Select */
  filterAttributesWithVariants(data) {
    let attributes = [];
    data.variantAttributesDirectory.forEach((attribute) => {
      let options = [];
      attribute.options.forEach((option) => {
        const hasMatch = data.variants.find((variant) => variant.variantAttributes[attribute.code] === option.value);
        if (hasMatch) {
          options.push(option);
        }
      });
      attributes.push({
        ...attribute,
        options,
      });
    });
    return attributes;
  },
  async loadFilteredProduct(code, isB2b) {
    const data = await this.fetchFullProduct({code, isB2b});
    const filteredVariantAttributesDirectory = this.filterAttributesWithVariants(data);
    return {
      ...data,
      variantAttributesDirectory: filteredVariantAttributesDirectory,
    };
  },
  async getProductFulfillmentOptions(code, options) {
    const response = await occInstance.get(`/products/${code}/fulfillments`, {
      params: options,
    });
    return response.data;
  },
  async fetchProductSpecifications(code) {
    const response = await occInstance.get(`/products/${code}/specifications`);
    return response.data;
  },
  async fetchProductTechData(code) {
    const response = await occInstance.get(`/products/technical/${code}`);
    return response.data;
  },
  async fetchProductSizingData(code, isB2Bsite, b2bUnitId) {
    let url = `/products/${code}/sizing`;
    if (isB2Bsite) {
      url += `?b2bUnitId=${b2bUnitId}`;
    }
    const response = await occInstance.get(url);
    return response.data;
  },
  async fetchProductDocuments(assetLocation, linkUrl) {
    const response = await occInstance.get('/products/processLink', {
      params: {
        assetLocation,
        linkUrl,
      },
    });
    return response.data;
  },
  async fetchProductSpecInfo(code) {
    const response = await occInstance.get(`/products/${code}/specs`);
    return response.data;
  },
};
export {ProductApi};
